import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChange,
  SimpleChanges
} from '@angular/core';
import { CalculatorsService } from '../calculators.service';
import { Observable, Subject } from 'rxjs';
import Utils from '@iv/shared/utils';
import { AppStore } from '@iv/store/app.store';
import * as Redux from 'redux';
import { AppState } from '@iv/store/app.reducer';
import { getCurrentTab } from '@iv/store/tabs/tabs.selectors';
import { Future } from '@iv/model/instrument';
import { PaymentWindowService } from '../../payment-window.service';
import { delay, takeUntil } from "rxjs/operators";
import { Destroyer } from "../../common/utils";
import { SearchCriteriaType } from "./enums/search-criteria-type.enum";

@Component({
  selector: 'iv-live-common-calculator',
  templateUrl: './common-calculator.component.html',
  styleUrls: ['./common-calculator.component.scss']
})
// TODO: Привести в порядок названия методов и свойств
export class CommonCalculatorComponent extends Destroyer implements OnInit, OnChanges, OnDestroy {

  @Input() instruments = [];
  @Input() stockName = '';
  @Input() value: string = '';
  @Input() stockSearchCriteria = '';
  @Input() autocompletePlaceholder: string;
  @Input() regionId: number;

  @Output() goButtonClicked = new EventEmitter<any>();
  @Output() modeClicked = new EventEmitter<any>();
  @Output() changeInstrument = new EventEmitter<any>();
  @Output() keyDownSearch: EventEmitter<KeyboardEvent> = new EventEmitter<KeyboardEvent>();
  @Output() returnRegionEvent = new EventEmitter<any>();
  @Output() showSpinnerEvent: EventEmitter<void> = new EventEmitter<void>();
  @Output() hideSpinnerEvent: EventEmitter<void> = new EventEmitter<void>();

  _stockName = '';
  advancedSearchVisible: boolean = false;
  isFutures: boolean = false;
  fieldNameWithId: string;

  advancedSearchPopupSize = {
    width: window.innerWidth > 680 ? 660 : window.innerWidth - 20,
    height: window.innerHeight > 590 ? 570 : window.innerHeight - 20
  };

  private isEOD = false;
  private searchCriteriaSubject: Subject<any> = new Subject();

  constructor(
    private calculatorsService: CalculatorsService,
    @Inject(AppStore) private store: Redux.Store<AppState>,
    private readonly paymentWindowService: PaymentWindowService
) {
    super();
  }

  ngOnInit() {
    this.paymentWindowService.showPaymentWindow();
    this.setFieldNameWithId();

    if (this.isFutures) {
      this.instruments = this.instruments.filter((future: Future) => future.regionId !== 5);
    }

    this.searchCriteriaSubject
      .pipe(delay(0), takeUntil(this.destroy$))
      .subscribe(stockId => {
        this.selectStock(this.findStockByCriteria(stockId));
      })
  }

  ngOnChanges(changes: SimpleChanges) {
    if (getCurrentTab(this.store.getState()).type.includes('future') && !this.isFutures) {
      this.isFutures = true;
    }
    if (this.stockName !== null && this.stockName !== ' ' && this.value === 'calc' && this._stockName !== this.stockName) {
      this._stockName = this.stockName;
      this.findStock();
    }

    this.handleSearchCriteriaChanges(changes['stockSearchCriteria']);
  }

  public ngOnDestroy() {
    this._stockName = null;
    this.value = null;
  }

  private setFieldNameWithId(): void {
    this.fieldNameWithId = this.isFutures ? 'underlyingId' : 'stockId';
  }

  switchMode() {
    if (this.regionId !== 1) return;
    this.isEOD = !this.isEOD;
    this.modeClicked.emit({isEod: this.isEOD, id: this.stockSearchCriteria});
    this.findStock();
  }

  selectStock(stockData): void {
    if (!stockData) return;
    this.stockSearchCriteria = stockData[this.fieldNameWithId];
    this._stockName = stockData.nameAndExchange;
    this.changeInstrument.emit(stockData);
    this.checkRegion(stockData[this.fieldNameWithId]);
  }

  openAdvancedSearch(): void {
    this.advancedSearchVisible = true;
  }

  findStock(): void {
    if (!this.stockSearchCriteria) return;
    const item = this.findStockByCriteria(this.stockSearchCriteria);
    const searchCriteria: SearchCriteriaType = this.defineSearchCriteriaType(this.stockSearchCriteria);

    switch (true) {
      case (!!item) : {
        this._stockName = item.nameAndExchange;
        this.stockSearchCriteria = item[this.fieldNameWithId];
        this.selectStock(item);
        break;
      }
      case (!item && searchCriteria === SearchCriteriaType.ID) : {
        this.checkRegion(this.stockSearchCriteria);
        break;
      }
      default : return;
    }
  }

  private checkRegion(id) {
    if (this.isFutures) { // TODO: Скорее всего для фьючерса тоже должен запрашивать регион, но пока у нас только eod - убрал
      this.goButtonClicked.emit({isEod: !!this.isEOD, id});
      return;
    }

    this.showSpinnerEvent.emit();
    this.getRegionId(id).subscribe((regionId: number) => {
      this.setRegionId(regionId);
      if (this.regionId !== 1) {
        this.isEOD = true;
        this.modeClicked.emit({isEod: this.isEOD, id: this.stockSearchCriteria});
      }
      this.returnRegionEvent.emit({region: this.regionId});
      this.hideSpinnerEvent.emit();
      this.goButtonClicked.emit({isEod: !!this.isEOD, id});
    });
  }

  selectAdvancedSearchStock(id: unknown): void {
    this.selectStock(this.findStockByCriteria(id));
    this.advancedSearchVisible = false;
  }

  getStockType(item: any) {
    return Utils.getStockType(item);
  }

  private getRegionId(stockId): Observable<number> {
    return this.calculatorsService[this.isFutures ? 'getFutRegion' : 'getRegion'](stockId);
  }

  private setRegionId(regionId: number): void {
    this.regionId = regionId;
    sessionStorage.setItem('currentStockRegion', regionId.toString());
  }

  private handleSearchCriteriaChanges(changes: SimpleChange): void {
    if (!changes || changes.firstChange || changes.currentValue === changes.previousValue) return;

    this.searchCriteriaSubject.next(changes.currentValue);
  }

  private findStockByCriteria(searchCriteria: any) {
    const searchCriteriaType: SearchCriteriaType = this.defineSearchCriteriaType(searchCriteria);

    switch (searchCriteriaType) {
      case SearchCriteriaType.SYMBOL: return this.instruments.find(item => item.symbol === searchCriteria.toUpperCase());
      case SearchCriteriaType.NAME_AND_EXCHANGE: return this.instruments.find(item => item.nameAndExchange === searchCriteria.toUpperCase());
      case SearchCriteriaType.ID: return this.instruments.find(item => item[this.fieldNameWithId] === searchCriteria);
    }
  }

  private defineSearchCriteriaType(searchCriteria: any): SearchCriteriaType {
    switch (true) {
      case (searchCriteria?.length < 6 && !searchCriteria.includes(':')) : return SearchCriteriaType.SYMBOL;
      case (searchCriteria?.length && searchCriteria.includes(':')) : return SearchCriteriaType.NAME_AND_EXCHANGE;
      default : return SearchCriteriaType.ID
    }
  }
}
